import { BASE_URL } from '../integrations/APIClient';

// Two first-party origins only: the website's linked API and its Azure Function App.
// A Static Web App without a linked API can answer unknown /api paths with
// index.html (HTTP 200). Treat that response as an unavailable route.
const KNOWN_FUNCTIONS_ORIGIN = 'https://chesshoodbackend.azurewebsites.net';
const API_ORIGINS = [...new Set(
  [BASE_URL, KNOWN_FUNCTIONS_ORIGIN]
    .map((origin) => String(origin || '').trim().replace(/[/]+$/, ''))
    .filter(Boolean)
)];

function errorFor(response, data) {
  const err = new Error(data?.error || data?.message || `Hood AI error (${response.status})`);
  err.status = response.status;
  err.data = data || {};
  return err;
}

function missingRoute(response, json) {
  if ([404, 405].includes(response.status)) return true;
  if (!response.ok) return false;
  const type = (response.headers.get('content-type') || '').toLowerCase();
  return type.includes('text/html') || !json || typeof json !== 'object' || Array.isArray(json);
}

async function requestRoute(route, payload, headers) {
  for (const origin of API_ORIGINS) {
    // A POST with a network failure is not safely retryable: a reply could
    // already have been generated and counted. Only a confirmed missing route
    // (404/405 or the site's index.html) permits trying the next origin.
    const response = await fetch(`${origin}${route}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });
    const text = await response.text();
    let data;
    try { data = text ? JSON.parse(text) : null; } catch { data = null; }
    if (missingRoute(response, data)) continue;
    if (!response.ok) throw errorFor(response, data);
    if (data.ok === false) {
      const err = new Error(data.error || 'Hood AI is temporarily unavailable.');
      err.data = data;
      throw err;
    }
    return data;
  }
  return null;
}

function legacyCoachPayload(payload, lang) {
  const board = payload.board || {};
  const task = payload.task || {};
  const playerMessage = String(payload.playerMessage || '').trim();
  const facts = [
    board.fen ? `FEN: ${board.fen}` : '',
    board.pgn ? `Moves: ${String(board.pgn).slice(-500)}` : '',
    board.lastMove ? `Last move: ${board.lastMove}` : '',
    board.evaluation ? `Evaluation: ${board.evaluation}` : '',
    board.bestMove ? `Stockfish best move: ${board.bestMove}` : '',
    board.openingName ? `Opening: ${board.openingName}` : '',
    task.title ? `Training: ${task.title}` : '',
  ].filter(Boolean).join('; ');
  const instruction = playerMessage
    ? `Answer the player's question directly: ${playerMessage}`
    : `You are coaching a chess game. Event: ${payload.event || 'task_start'}. Give one concise, accurate and useful coaching tip.`;
  return {
    mode: 'general_chess',
    lang,
    message: `${instruction}\nCurrent chess context: ${facts}. Only use Stockfish values that are provided.`,
    context: { quickContext: { board: { fen: board.fen, pgn: board.pgn, lastMove: board.lastMove, evaluation: board.evaluation, bestMove: board.bestMove }, training: task.title || '' } },
  };
}

export async function requestHoodCoach({ payload, token, lang = 'en', betaMode = false }) {
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    Authorization: `Bearer ${token}`,
    'X-Lang': lang,
    ...(betaMode ? { 'X-Chesshood-Beta': '1' } : {}),
  };

  const primary = await requestRoute('/api/hoodAI/trainer-agent', { ...payload, lang }, headers);
  if (primary) return primary;

  // The older authenticated Hood endpoint is an emergency fallback only if
  // neither first-party host has the new trainer route deployed. Never use it
  // after an access denial, a provider failure, or a timeout.
  const legacy = await requestRoute('/api/hoodAI', legacyCoachPayload(payload, lang), headers);
  if (legacy && (legacy.message || legacy.speech || legacy.coachText)) {
    return {
      ...legacy,
      speech: legacy.speech || legacy.coachText || legacy.message,
      serviceFallback: 'legacy_hood_ai',
    };
  }

  const err = new Error(
    lang === 'pl' ? 'Serwer Hood AI nie udostępnia jeszcze trenera. Spróbuj ponownie po aktualizacji.'
      : lang === 'es' ? 'El servidor Hood AI todavía no ofrece el entrenador. Inténtalo de nuevo tras la actualización.'
        : 'Hood AI trainer is not available on the server yet. Please retry after the update.'
  );
  err.status = 404;
  throw err;
}
